import { useState, useEffect } from 'react';
import { Copy, Shield, Calendar as CalendarIcon, QrCode, Check, Link2, Eye, EyeOff } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, addDays } from 'date-fns';
import { useToast } from '@/hooks/use-toast';

export type AccessLevel = 'public' | 'private' | 'restricted';

export interface ShareData {
  shareToken: string;
  shareUrl: string;
  accessLevel: AccessLevel;
  hasPassword: boolean;
  expiresAt: string | null;
  createdAt: string;
}

export interface ShareConfig {
  password?: string;
  expiresAt?: string;
  accessLevel: AccessLevel;
}

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presentationId: string;
  presentationTitle: string;
  existingShare?: ShareData;
  onCreateShare: (config: ShareConfig) => Promise<ShareData>;
  onUpdateShare: (shareId: string, config: ShareConfig) => Promise<ShareData>;
  onDeleteShare?: (shareId: string) => Promise<void>;
}

export function ShareDialog({
  open,
  onOpenChange,
  presentationId: _presentationId, // Used for future API calls
  presentationTitle,
  existingShare,
  onCreateShare,
  onUpdateShare,
  onDeleteShare,
}: ShareDialogProps) {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);

  // Share configuration
  const [accessLevel, setAccessLevel] = useState<AccessLevel>('public');
  const [password, setPassword] = useState('');
  const [expirationDays, setExpirationDays] = useState<string>('never');
  const [customDate, setCustomDate] = useState<Date>();

  useEffect(() => {
    if (existingShare) {
      setShareUrl(existingShare.shareUrl);
      setAccessLevel(existingShare.accessLevel);

      // Calculate expiration days from existing share
      if (existingShare.expiresAt) {
        const expiresDate = new Date(existingShare.expiresAt);
        const now = new Date();
        const diffDays = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays <= 1) setExpirationDays('1');
        else if (diffDays <= 7) setExpirationDays('7');
        else if (diffDays <= 30) setExpirationDays('30');
        else setCustomDate(expiresDate);
      }
    }
  }, [existingShare]);

  const handleCopyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast({
        title: 'Link copied!',
        description: 'Share link has been copied to your clipboard.',
      });

      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({
        title: 'Failed to copy',
        description: 'Please manually copy the link.',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateShare = async () => {
    setIsLoading(true);
    try {
      let expiresAt: string | undefined;

      if (expirationDays === 'custom' && customDate) {
        expiresAt = customDate.toISOString();
      } else if (expirationDays !== 'never') {
        const days = parseInt(expirationDays);
        expiresAt = addDays(new Date(), days).toISOString();
      }

      const config: ShareConfig = {
        accessLevel,
        password: password || undefined,
        expiresAt,
      };

      let shareData: ShareData;

      if (existingShare) {
        shareData = await onUpdateShare(existingShare.shareToken, config);
      } else {
        shareData = await onCreateShare(config);
      }

      setShareUrl(shareData.shareUrl);

      toast({
        title: existingShare ? 'Share updated!' : 'Share created!',
        description: 'Your presentation is now ready to share.',
      });
    } catch (error) {
      toast({
        title: 'Failed to create share',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteShare = async () => {
    if (!existingShare || !onDeleteShare) return;

    setIsLoading(true);
    try {
      await onDeleteShare(existingShare.shareToken);
      setShareUrl('');
      toast({
        title: 'Share deleted',
        description: 'The share link has been removed.',
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: 'Failed to delete share',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges =
    accessLevel !== (existingShare?.accessLevel || 'public') ||
    password !== '' ||
    expirationDays !== 'never' ||
    customDate !== undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Share Presentation
          </DialogTitle>
          <DialogDescription>
            Create a shareable link for "{presentationTitle}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Share URL Section */}
          {shareUrl && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <Label>Share Link</Label>
              <div className="flex gap-2">
                <Input
                  value={shareUrl}
                  readOnly
                  className="font-mono text-sm"
                  onClick={(e) => e.currentTarget.select()}
                />
                <Button
                  size="icon"
                  variant="outline"
                  onClick={handleCopyToClipboard}
                  className="shrink-0"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Access Level */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Access Level
            </Label>
            <Select value={accessLevel} onValueChange={(value: AccessLevel) => setAccessLevel(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="public">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    <span>Public</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      Anyone with link
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="private">
                  <div className="flex items-center gap-2">
                    <EyeOff className="w-4 h-4" />
                    <span>Private</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      Password required
                    </span>
                  </div>
                </SelectItem>
                <SelectItem value="restricted">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>Restricted</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      Specific users
                    </span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Password Protection */}
          {accessLevel === 'private' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-2"
            >
              <Label>Password</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password (optional)"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave empty to allow open access with link
              </p>
            </motion.div>
          )}

          {/* Expiration Date */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4" />
              Expiration
            </Label>
            <div className="flex gap-2">
              <Select value={expirationDays} onValueChange={setExpirationDays}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select expiration" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never</SelectItem>
                  <SelectItem value="1">1 Day</SelectItem>
                  <SelectItem value="7">7 Days</SelectItem>
                  <SelectItem value="30">30 Days</SelectItem>
                  <SelectItem value="custom">Custom Date</SelectItem>
                </SelectContent>
              </Select>

              {expirationDays === 'custom' && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-[140px]">
                      {customDate ? (
                        format(customDate, 'MMM dd, yyyy')
                      ) : (
                        'Pick date'
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={customDate}
                      onSelect={setCustomDate}
                      initialFocus
                      disabled={(date: Date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>
          </div>

          {/* QR Code Option */}
          <div className="flex items-center gap-2 p-3 rounded-lg border border-dashed">
            <QrCode className="w-5 h-5 text-muted-foreground" />
            <div className="flex-1">
              <p className="text-sm font-medium">QR Code</p>
              <p className="text-xs text-muted-foreground">
                Generate QR code for easy sharing (coming soon)
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          <Button
            onClick={handleGenerateShare}
            disabled={isLoading || !hasChanges}
            className="w-full"
          >
            {isLoading ? (
              'Creating share...'
            ) : existingShare ? (
              'Update Share'
            ) : (
              'Create Share Link'
            )}
          </Button>

          {shareUrl && (
            <Button
              variant="outline"
              onClick={handleCopyToClipboard}
              className="w-full"
            >
              <Copy className="w-4 h-4 mr-2" />
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
          )}

          {existingShare && onDeleteShare && (
            <Button
              variant="ghost"
              onClick={handleDeleteShare}
              disabled={isLoading}
              className="w-full text-destructive hover:text-destructive"
            >
              Delete Share
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
